import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Slider from "react-slick";
import { FaStar, FaStarHalfAlt, FaRegSmile, FaGlobeAmericas, FaUsers } from "react-icons/fa";
import { RiCheckboxCircleFill, RiCheckboxCircleLine } from "react-icons/ri";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import MainLayout from "@/Component/MainLayout";
import Breadcrumbs from "@/Component/Common/Breadcrumbs";
import DownloadNH from "@/Component/Common/DownloadNH";
import { ReviewCard, ReviewsMarquee } from "@/Component/Common/CustomerReviews";
import WriteReviewModal from "@/Component/Common/CustomerReviews/WriteReviewModal";
import CommonHeader from "@/Common/CommonHeader/CommonHeader";
import useIsDesktop from "@/hooks/useIsDesktop";
import useSlickArrows from "@/hooks/useSlickArrows";
import { fetchReviewsList } from "@/services/api/common";
import {
  collectTravelerGalleryImages,
  formatSlugLabel,
  getRatingBreakdownRows,
} from "@/Component/Common/CustomerReviews/reviewUtils";
import reviewStyles from "@/Component/Common/CustomerReviews/CustomerReviews.module.css";
import {
  getTestimonialReviewTabs,
  formatStatCount,
  formatReviewCount,
} from "@/utils/reviewHelpers";
import { isIndianUser } from "@/utils/getCountryCode";
import { withRegionPath } from "@/utils/session";
import styles from "./Testimonials.module.css";
import PageHero from "@/Component/Common/PageHero";
import { scrollToSectionElement } from "@/utils/scrollToSection";

const REVIEW_SLIDES_TO_SHOW = 2;

function SummaryStars({ count }) {
  const rating = Math.max(0, Math.min(5, Number(count) || 0));

  return (
    <div className={styles.summaryStars} aria-label={`${rating.toFixed(1)} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, index) => {
        const starValue = index + 1;
        if (rating >= starValue) {
          return <FaStar key={index} color="#FFB700" size={18} />;
        }
        if (rating >= starValue - 0.5) {
          return <FaStarHalfAlt key={index} color="#FFB700" size={18} />;
        }
        return <FaStar key={index} color="#E8E8E8" size={18} />;
      })}
    </div>
  );
}

function TrustStats() {

  const stats = [
    {
      icon: <FaStar className={styles.statIcon} color="#FFB700" size={26} />,
      value: `4.5/5`,
      label: "Overall Rating",
    },
    {
      icon: <FaRegSmile className={styles.statIcon} color="#D92027" size={26} />,
      value: `5K+`,
      label: "Happy Travelers",
    },
    {
      icon: (
        <FaUsers
          className={styles.statIcon}
          color="#D92027"
          size={26}
        />
      ),
      value: "100+",
      label: "Partners",
    },
    {
      icon: (
        <FaGlobeAmericas
          className={styles.statIcon}
          color="#D92027"
          size={26}
        />
      ),
      value: "120+",
      label: "Destinations",
    },
  ];

  return (
    <section className={`standard-padding ${styles.statsSection}`}>
      <div className={styles.statsHeader}>
        <h2 className="standard-heading">{isIndianUser() ? "Trusted by millions, loved around the world." : "Making Travelers Smile in Every Corner of the World"}</h2>
        <p className="standard-subheading">
          {isIndianUser() ?
            "Real reviews from global travelers who explore the world with us."
            :
            "Thousands of journeys, countless memories, and genuine reviews from explorers worldwide."
          }
        </p>
      </div>
      <div className={styles.statsGrid}>
        {stats.map((item) => (
          <div key={item.label} className={styles.statCard}>
            <span className={styles.statIcon} aria-hidden="true">
              {item.icon}
            </span>
            <p className={styles.statValue}>{item.value}</p>
            <p className={styles.statLabel}>{item.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function RatingBreakdown({ summary }) {
  const breakdownRows = getRatingBreakdownRows(
    summary?.ratingsBreakdown,
    summary?.totalRatings || summary?.totalReviews
  );

  return (
    <div className={styles.breakdownList}>
      {breakdownRows.map(({ star, percent }) => (
        <div key={star} className={styles.breakdownRow}>
          <span className={styles.breakdownStar}>{star} Star</span>
          <div className={styles.breakdownTrack}>
            <span className={styles.breakdownFill} style={{ width: `${percent}%` }} />
          </div>
          <span className={styles.breakdownCount}>{Math.round(percent)}%</span>
        </div>
      ))}
    </div>
  );
}

function CapturedMomentsMarquee({ images = [] }) {
  const loop = useMemo(
    () => (images.length ? [...images, ...images] : []),
    [images]
  );
  const marqueeDuration = Math.max(70, images.length * 4);

  if (!images.length) return null;

  return (
    <div className={styles.galleryMarquee} aria-label="Real Journeys. Real Moments.">
      <div
        className={styles.galleryTrack}
        style={{ "--gallery-duration": `${marqueeDuration}s` }}
      >
        {loop.map((image, index) => (
          <div key={`${image}-${index}`} className={styles.galleryMarqueeItem}>
            <img src={image} alt="" loading="lazy" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Testimonials({
  initialReviewsByModule = {},
}) {
  const contentRef = useRef(null);
  const sliderRef = useRef(null);
  const { isDesktop, mounted } = useIsDesktop(900);
  const reviewTabs = useMemo(() => getTestimonialReviewTabs(isIndianUser()), []);
  const [activeModule, setActiveModule] = useState(reviewTabs[0]?.id || "Holiday");
  const [reviewsByModule, setReviewsByModule] = useState(initialReviewsByModule);
  const loadedModulesRef = useRef(new Set(Object.keys(initialReviewsByModule)));
  const [loadingModule, setLoadingModule] = useState(null);
  const [showWriteModal, setShowWriteModal] = useState(false);
  const reviewsList = reviewsByModule[activeModule] || null;
  const summary = reviewsList?.summary || {};
  const reviews = reviewsList?.reviews || [];
  const galleryImages = useMemo(() => {
    const allReviews = Object.values(reviewsByModule).flatMap(
      (list) => list?.reviews || []
    );
    return collectTravelerGalleryImages(allReviews, 30);
  }, [reviewsByModule]);

  const loadReviews = useCallback((moduleType, force = false) => {
    if (!moduleType) return Promise.resolve();

    if (!force && loadedModulesRef.current.has(moduleType)) {
      return Promise.resolve();
    }

    setLoadingModule(moduleType);
    return fetchReviewsList({ moduleType })
      .then((response) => {
        loadedModulesRef.current.add(moduleType);
        setReviewsByModule((prev) => ({
          ...prev,
          [moduleType]: response?.data ?? { reviews: [], summary: {} },
        }));
      })
      .catch((error) => {
        console.error("Failed to load testimonials reviews:", error);
        loadedModulesRef.current.add(moduleType);
        setReviewsByModule((prev) => ({
          ...prev,
          [moduleType]: { reviews: [], summary: {} },
        }));
      })
      .finally(() => {
        setLoadingModule(null);
      });
  }, []);

  useEffect(() => {
    const allowedIds = reviewTabs.map((tab) => tab.id);
    if (!allowedIds.includes(activeModule)) {
      setActiveModule(reviewTabs[0]?.id || "Holiday");
    }
  }, [reviewTabs, activeModule]);

  useEffect(() => {
    loadReviews(activeModule);
  }, [activeModule, loadReviews]);

  const handleHeroClick = () => {
    if (!contentRef.current) return;
    scrollToSectionElement(contentRef.current);
  };

  const fewReviewSlides = reviews.length <= REVIEW_SLIDES_TO_SHOW;
  const { canPrev, canNext, slickArrowEvents } = useSlickArrows(
    reviews.length,
    REVIEW_SLIDES_TO_SHOW,
    activeModule
  );

  const reviewSliderSettings = {
    dots: false,
    infinite: false,
    centerMode: false,
    speed: 400,
    slidesToShow: REVIEW_SLIDES_TO_SHOW,
    slidesToScroll: 1,
    arrows: false,
    swipe: false,
    draggable: false,
    touchMove: false,
    ...slickArrowEvents,
  };

  useEffect(() => {
    if (typeof sliderRef.current?.slickGoTo === "function") {
      sliderRef.current.slickGoTo(0);
    }
  }, [activeModule, reviews.length]);

  const handleTabChange = (moduleId) => {
    setActiveModule(moduleId);
    if (typeof sliderRef.current?.slickGoTo === "function") {
      sliderRef.current.slickGoTo(0);
    }
  };

  const isLoading = loadingModule === activeModule && !reviewsList;

  return (
    <MainLayout hideHeader>
      <div className={styles.page}>
        {/* <CommonHeader
          title="Loved By Travellers, Trusted For Every Journey."
          buttonText="Read What They Say!"
          buttonLink="#testimonials-reviews"
          bgImage="/images/Banner/blog-banner.jpg"
          onButtonClick={handleHeroClick}
        /> */}
        <PageHero
          webImageUrl={isIndianUser() ? "/assets/images/TestimonialsBannerIndia.webp" : "/assets/images/TestimonialsBanner.webp"}
          alt="Testimonial"
          scrollTargetRef={contentRef}
        />
        <div ref={contentRef}>
          <Breadcrumbs />
        </div>

        <TrustStats summary={summary} />

        <section
          id="testimonials-reviews"
          className={`standard-padding ${styles.reviewsSection}`}
        >
          <div className={`${reviewStyles.header} ${styles.reviewsHeader}`}>
            <div className={reviewStyles.headingBlock}>
              <h2 className="standard-heading">{isIndianUser() ? "Real Stories from Real Travelers" : "Words from Travelers"}</h2>
              <p className="standard-subheading">
                {isIndianUser() ? "See how we help explorers build memorable holidays!" : "Read honest thoughts from our holidaymakers."}
              </p>
            </div>
            {reviewTabs.length > 1 ? (
              <div className={styles.tabsRow}>
                {reviewTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`${styles.tabBtn} ${activeModule === tab.id ? styles.tabBtnActive : ""
                      }`}
                    onClick={() => handleTabChange(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {isLoading ? (
            <p className="nodata">Loading reviews...</p>
          ) : reviews.length === 0 ? (
            <p className="nodata">No data found</p>
          ) : (
            <div className={styles.reviewsLayout}>
              <aside className={styles.summaryCard}>
                <div className={styles.summaryTop}>
                  <span className={styles.summaryScore}>
                    {Number(summary.averageRating || 0).toFixed(1)}
                  </span>
                  <SummaryStars count={summary.averageRating} />
                  <p className={styles.summaryMeta}>
                    Based on {formatReviewCount(summary.totalReviews || 0)} reviews
                  </p>
                </div>
                <RatingBreakdown summary={summary} />
              </aside>

              {mounted && isDesktop ? (
                <div
                  className={`${styles.reviewsSliderWrap} ${fewReviewSlides ? styles.alignLeft : ""
                    }`}
                >
                  <button
                    type="button"
                    className={`headerArrowBtn ${styles.sidePrev}`}
                    aria-label="Previous reviews"
                    disabled={!canPrev}
                    onClick={() => sliderRef.current?.slickPrev()}
                  >
                    <MdChevronLeft size={22} />
                  </button>

                  <button
                    type="button"
                    className={`headerArrowBtn ${styles.sideNext}`}
                    aria-label="Next reviews"
                    disabled={!canNext}
                    onClick={() => sliderRef.current?.slickNext()}
                  >
                    <MdChevronRight size={22} />
                  </button>

                  <Slider ref={sliderRef} {...reviewSliderSettings}>
                    {reviews.map((review) => (
                      <div key={review.reviewId} className={styles.slickSlide}>
                        <ReviewCard
                          review={review}
                          slugLabel={formatSlugLabel(review.slug)}
                          className={reviewStyles.cardGrid}
                          textClassName={reviewStyles.cardGridText}
                          showQuotes
                        />
                      </div>
                    ))}
                  </Slider>
                </div>
              ) : (
                <ReviewsMarquee
                  reviews={reviews}
                  showQuotes
                  getSlugLabel={(review) => formatSlugLabel(review.slug)}
                />
              )}
            </div>
          )}
        </section>

        <div className="standard-padding">
          <section className={styles.shareStorySection}>
            <div className={styles.shareStoryInner}>
              <div className={styles.shareStoryCopy}>
                <h2 className="standard-heading">{isIndianUser() ? "Share Your Holiday Experience!" : "Inspire Other Travelers"}</h2>
                <p className="standard-subheading">
                  {isIndianUser() ?
                    "Help fellow travelers plan their dream getaway by leaving your honest review."
                    :
                    "Your words matter. Leave a review and share your favorite moments."
                  }
                </p>
              </div>
              <button
                type="button"
                className={styles.shareStoryBtn}
                onClick={() => setShowWriteModal(true)}
              >
                Write a Review
              </button>
            </div>
          </section>
        </div>

        {galleryImages.length > 0 ? (
          <section className={styles.gallerySection}>
            <div className={styles.galleryHeader}>
              <h2 className="standard-heading">Real Snaps. Real Stories. </h2>
            </div>
            <CapturedMomentsMarquee images={galleryImages} />
          </section>
        ) : null}

        <section className={`standard-padding ${styles.exploreSection}`}>
          <h2 className="standard-heading">{isIndianUser() ? "Create Your Own Travel Story" : "Next Stop: Vacation"}</h2>
          <p className="standard-subheading">
            {isIndianUser() ?
              "From custom flight deals to guided tour packages, let our travel experts design your trip."
              :
              "Discover customized itineraries, verified hotels, and complete travel assistance designed around how you love to explore."
            }
          </p>
          <a href={withRegionPath("/holiday")} className={styles.exploreBtn}>
            Start exploring
          </a>
        </section>

        <DownloadNH />
      </div>

      <WriteReviewModal
        open={showWriteModal}
        onClose={() => setShowWriteModal(false)}
        moduleType={activeModule}
        allowModuleSelect
        redirectOnSuccess
        onSuccess={(submittedModule) => loadReviews(submittedModule || activeModule, true)}
      />
    </MainLayout>
  );
}
